import { useState } from "react";
import { Button, Col, Form, Row, Tag } from "antd";
import { Settings, Edit, AlertTriangle, Archive, X } from "lucide-react";
import { format } from "date-fns";
import dayjs from "dayjs";
import {
  buildCommunityPayload,
  getDefaultFormValues,
  getDefaultLocations,
} from "../../commons/community.helpers";
import CommunityForm from "Components/CommunityForm/CommunityForm";

function FieldRow({ label, children }) {
  if (!children) return null;
  return (
    <Col xs={24} sm={12}>
      <div
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          background: "var(--color-bg-muted, rgba(0,0,0,0.03))",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: "var(--color-text-tertiary)",
            marginBottom: 2,
          }}
        >
          {label}
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, wordBreak: "break-word" }}>
          {children}
        </div>
      </div>
    </Col>
  );
}

function FullFieldRow({ label, children }) {
  if (children === null || children === undefined || children === "")
    return null;
  return (
    <Col xs={24}>
      <div
        style={{
          padding: "10px 12px",
          borderRadius: 8,
          background: "var(--color-bg-muted, rgba(0,0,0,0.03))",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: "var(--color-text-tertiary)",
            marginBottom: 2,
          }}
        >
          {label}
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
          {children}
        </div>
      </div>
    </Col>
  );
}

function SectionLabel({ children }) {
  return (
    <Col xs={24}>
      <p
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--color-text-tertiary)",
          margin: "12px 0 4px",
        }}
      >
        {children}
      </p>
    </Col>
  );
}

function SettingsTab({ community, masterData, onSave, onArchive }) {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editLocations, setEditLocations] = useState([]);
  const [showTC, setShowTC] = useState(false);

  const enterEditMode = () => {
    setEditLocations(getDefaultLocations(community));
    const hasTC = !!community.terms_and_conditions?.title;
    setShowTC(hasTC);
    setIsEditing(true);
    // Defer setFieldsValue so the Form (including Form.List) is mounted first,
    // giving Ant Design stable internal keys before values are injected.
    setTimeout(() => {
      form.setFieldsValue(getDefaultFormValues(community));
    }, 0);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setShowTC(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields({ recursive: true });
    } catch {
      setTimeout(() => {
        const el = document.querySelector(".ant-form-item-has-error");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      return;
    }
    const payload = buildCommunityPayload(values, editLocations);
    if (payload.operational_locations.length === 0) return;
    setSaving(true);
    const ok = await onSave(payload);
    setSaving(false);
    if (ok) setIsEditing(false);
  };

  return (
    <div className="space-y-6 animate-in">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Settings className="h-4 w-4 text-primary" />
          <h3 className="font-bold text-sm uppercase tracking-wider text-muted-foreground">
            Community Settings
          </h3>
        </div>
        {!isEditing ? (
          <Button type="default" size="small" onClick={enterEditMode}>
            <Edit className="mr-2 h-4 w-4" /> Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              size="small"
              onClick={cancelEdit}
              icon={<X className="h-4 w-4" />}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="small"
              loading={saving}
              onClick={handleSubmit}
            >
              Save changes
            </Button>
          </div>
        )}
      </div>

      {/* ── Read-only detail card ───────────────────────────────────── */}
      {!isEditing && (
        <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm text-sm">
          <Row gutter={[0, 0]}>
            {/* Basic */}
            <SectionLabel>Basic</SectionLabel>
            <FieldRow label="Community ID">{community.community_id}</FieldRow>
            <FieldRow label="Name">{community.name}</FieldRow>
            <FieldRow label="Display name">{community.display_name}</FieldRow>
            <FieldRow label="Active">
              {community.is_active ? "Yes" : "No"}
            </FieldRow>
            <FieldRow label="Verification">
              {community.verification_status}
            </FieldRow>
            <FullFieldRow label="Description">
              {community.description}
            </FullFieldRow>
            {community.collection_tags?.length > 0 && (
              <FullFieldRow label="Tags">
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    marginTop: 2,
                  }}
                >
                  {community.collection_tags.map((t) => (
                    <Tag key={t}>{t}</Tag>
                  ))}
                </div>
              </FullFieldRow>
            )}

            {/* Contact */}
            <SectionLabel>Contact</SectionLabel>
            <FieldRow label="Phone numbers">
              {community.contact_details?.phone_numbers?.join(", ")}
            </FieldRow>
            <FieldRow label="Emails">
              {community.contact_details?.email?.join(", ")}
            </FieldRow>
            <FieldRow label="Websites">
              {community.contact_details?.website?.join(", ")}
            </FieldRow>
            {community.contact_details?.social_links?.length > 0 && (
              <FullFieldRow label="Social links">
                {community.contact_details.social_links
                  .map((s) => `${s.label}: ${s.url}`)
                  .join(" · ")}
              </FullFieldRow>
            )}

            {/* Locations */}
            {community.operational_locations?.length > 0 && (
              <>
                <SectionLabel>Operational locations</SectionLabel>
                {community.operational_locations.map((l, i) => (
                  <FieldRow
                    key={i}
                    label={l.city_name || l.city_code}
                  >
                    {(l.areas ?? l.area_codes ?? []).join(", ")}
                  </FieldRow>
                ))}
              </>
            )}

            {/* Activities */}
            {(community.activities?.length > 0 ||
              community.what_we_do?.length > 0) && (
              <>
                <SectionLabel>Activities</SectionLabel>
                <FieldRow label="Activities">
                  {community.activities?.map((a) => a.name).join(", ")}
                </FieldRow>
                <FieldRow label="What we do">
                  {community.what_we_do?.map((w) => w.name).join(", ")}
                </FieldRow>
              </>
            )}

            {/* Community Lead Names */}
            {community.owner_metadata?.length > 0 && (
              <>
                <SectionLabel>Community Lead Names</SectionLabel>
                {community.owner_metadata.map((owner, i) => (
                  <Col key={i} xs={24} style={{ marginBottom: 8 }}>
                    <div
                      style={{
                        borderRadius: 10,
                        padding: "8px 4px 0",
                      }}
                    >
                      <Row gutter={[0, 0]}>
                        <FieldRow label="Name">{owner.name}</FieldRow>
                        <FieldRow label="Phone">{owner.phone_numbers}</FieldRow>
                        <FieldRow label="Hosting since">
                          {owner.hosting_since &&
                            dayjs(owner.hosting_since).format("DD MMM YYYY")}
                        </FieldRow>
                        <FullFieldRow label="Description">
                          {owner.description}
                        </FullFieldRow>
                      </Row>
                    </div>
                  </Col>
                ))}
              </>
            )}

            {/* Terms & Conditions */}
            {community.terms_and_conditions && (
              <>
                <SectionLabel>Terms &amp; Conditions</SectionLabel>
                <FieldRow label="Title">
                  {community.terms_and_conditions.title}
                </FieldRow>
                <FieldRow label="Subtitle">
                  {community.terms_and_conditions.sub_title}
                </FieldRow>
                {community.terms_and_conditions.sections?.map((section, i) => (
                  <FullFieldRow key={i} label={section.heading}>
                    {section.content}
                  </FullFieldRow>
                ))}
              </>
            )}

            {/* Audit */}
            <SectionLabel>Audit</SectionLabel>
            <FieldRow label="Created">
              {community.audit?.created_at &&
                format(new Date(community.audit.created_at), "MMM d, yyyy")}
            </FieldRow>
            <FieldRow label="Created by">
              {community.audit?.created_by}
            </FieldRow>
            <FieldRow label="Last updated">
              {community.audit?.updated_at &&
                format(
                  new Date(community.audit.updated_at),
                  "MMM d, yyyy HH:mm"
                )}
            </FieldRow>
            <FieldRow label="Updated by">
              {community.audit?.updated_by}
            </FieldRow>
          </Row>
        </div>
      )}

      {/* ── Edit form ───────────────────────────────────────────────── */}
      {isEditing && (
        <CommunityForm
          form={form}
          locations={editLocations}
          setLocations={setEditLocations}
          masterData={masterData}
          mode="edit"
          showTC={showTC}
          setShowTC={setShowTC}
        />
      )}

      {/* ── Danger zone ─────────────────────────────────────────────── */}
      {community.verification_status === "VERIFIED" && (
        <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-6 shadow-sm space-y-4">
          <div className="flex items-center space-x-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <h3 className="font-bold text-lg">Danger Zone</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {community.is_active
              ? "Mark inactive updates is_active via the portal API. You can re-activate the community at any time."
              : "This community is currently inactive. You can mark it active again to restore visibility."}
          </p>
          <div className="flex flex-wrap gap-2">
            {community.is_active ? (
              <Button
                type="default"
                size="small"
                className="text-amber-600 border-amber-300/70"
                onClick={() => onArchive(false)}
              >
                <Archive className="mr-2 h-4 w-4" /> Mark inactive
              </Button>
            ) : (
              <Button
                type="default"
                size="small"
                className="text-emerald-600 border-emerald-300/70"
                onClick={() => onArchive(true)}
              >
                <Archive className="mr-2 h-4 w-4" /> Mark active
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsTab;
